import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BatteryMedium,
  CheckCircle,
  ClipboardText,
  DeviceMobile,
  Pulse,
  TreeStructure,
  WarningCircle,
  WifiHigh,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/auth";
import { DataFreshness, Drawer, Modal, SearchInput, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime, formatRelativeTime, formatTaskType } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { AlertSummary, DashboardSummary, Device } from "../../lib/types";

function isAttentionDevice(device: Device) {
  return Boolean(
    device.tokenExpired ||
      device.tokenExpiringSoon ||
      ["disabled", "unbound"].includes(device.status),
  );
}

export function TaskDispatchModal({
  device,
  onClose,
}: {
  device: Device;
  onClose(): void;
}) {
  const { request, user } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [taskType, setTaskType] = useState("check_social_status");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      request("/api/tasks", {
        method: "POST",
        body: JSON.stringify({
          tenantGuid: user?.tenantGuid,
          taskType,
          targetType: "device",
          targetDeviceIds: [device.id],
          payload: { source: "admin-web" },
          requiresApproval,
          approvalReason: requiresApproval ? reason : undefined,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notify(
        requiresApproval ? "任务已提交审批" : "任务已下发",
        `${formatTaskType(taskType)} · 设备 #${device.id}`,
      );
      onClose();
    },
  });

  return (
    <Modal
      title="下发设备任务"
      description={`${device.info?.model || `设备 #${device.id}`} · ${device.account?.username || "未分配账号"}`}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>
            取消
          </button>
          <button
            className="primary-button"
            disabled={mutation.isPending || (requiresApproval && !reason.trim())}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending
              ? "正在提交…"
              : requiresApproval
                ? "提交审批"
                : "立即下发"}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <label>
          <span>任务类型</span>
          <select value={taskType} onChange={(event) => setTaskType(event.target.value)}>
            <option value="check_social_status">检查微信/企微状态</option>
            <option value="capture_accessibility_snapshot">采集无障碍快照</option>
            <option value="refresh_app_inventory">刷新应用清单</option>
            <option value="sync_contacts">同步联系人</option>
            <option value="upload_logs">上传诊断日志</option>
          </select>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(event) => setRequiresApproval(event.target.checked)}
          />
          <span>
            <strong>需要审批后执行</strong>
            <small>任务将在审批通过后进入派发队列。</small>
          </span>
        </label>
        {requiresApproval && (
          <label>
            <span>审批理由</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="说明任务目的和影响范围"
            />
          </label>
        )}
        {mutation.isError && (
          <p className="form-error">任务提交失败，请检查设备状态和账号权限。</p>
        )}
      </div>
    </Modal>
  );
}

export function DeviceDetail({
  deviceId,
  onClose,
  onDispatch,
}: {
  deviceId: string;
  onClose(): void;
  onDispatch(device: Device): void;
}) {
  const detail = useApiQuery<Device>(
    ["device", deviceId],
    `/api/devices/${deviceId}`,
  );
  const device = detail.data;

  return (
    <Drawer
      title={device?.info?.model || `设备 #${deviceId}`}
      subtitle={device?.info?.deviceSn || "正在读取设备详情"}
      onClose={onClose}
    >
      {detail.isPending ? (
        <div className="drawer-loading">正在加载设备信息…</div>
      ) : detail.isError || !device ? (
        <div className="drawer-loading error">设备详情加载失败。</div>
      ) : (
        <>
          <div className="device-profile">
            <span><DeviceMobile size={29} /></span>
            <div>
              <strong>{device.info?.model || `设备 #${device.id}`}</strong>
              <small>{device.account?.username || "未绑定员工账号"}</small>
            </div>
            <StatusTag
              value={isAttentionDevice(device) ? "warning" : device.status}
            />
          </div>
          <dl className="detail-list">
            <div><dt>设备编号</dt><dd>{device.id}</dd></div>
            <div><dt>设备序列号</dt><dd>{device.info?.deviceSn || "—"}</dd></div>
            <div><dt>系统版本</dt><dd>Android {device.info?.androidVersion || "—"}</dd></div>
            <div><dt>客户端版本</dt><dd>{device.info?.appVersion || "—"}</dd></div>
            <div><dt>最近心跳</dt><dd>{formatDateTime(device.lastSeenAt)}</dd></div>
            <div><dt>当前网关</dt><dd>{device.lastGatewayId || "—"}</dd></div>
            <div><dt>Token 到期</dt><dd>{formatDateTime(device.deviceTokenExpiresAt)}</dd></div>
          </dl>
          <section className="drawer-section">
            <h3>数据与执行概览</h3>
            <div className="count-grid">
              <div><strong>{device.counts?.taskResults || 0}</strong><span>任务结果</span></div>
              <div><strong>{device.counts?.contacts || 0}</strong><span>联系人</span></div>
              <div><strong>{device.counts?.messages || 0}</strong><span>消息</span></div>
              <div><strong>{device.counts?.apps || 0}</strong><span>应用</span></div>
            </div>
          </section>
          <button className="primary-button drawer-action" onClick={() => onDispatch(device)}>
            <ClipboardText size={18} />
            下发任务
          </button>
        </>
      )}
    </Drawer>
  );
}

export function OverviewPage() {
  const polling = Number(import.meta.env.VITE_QUERY_POLL_INTERVAL_MS || 10000);
  const devicesQuery = useApiQuery<Device[]>(
    ["devices", "overview"],
    "/api/devices?limit=200",
    { refetchInterval: polling },
  );
  const alertsQuery = useApiQuery<AlertSummary>(
    ["alerts", "summary"],
    "/api/alerts/summary",
    { refetchInterval: polling },
  );
  const summaryQuery = useApiQuery<DashboardSummary>(
    ["dashboard", "summary"],
    "/api/dashboard/summary",
    { refetchInterval: polling },
  );
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dispatchDevice, setDispatchDevice] = useState<Device | null>(null);

  const devices = devicesQuery.data || [];
  const filteredDevices = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return devices.filter((device) => {
      const matchesQuery =
        !normalized ||
        [
          device.id,
          device.info?.model,
          device.info?.deviceSn,
          device.account?.username,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      const matchesStatus =
        status === "all" ||
        (status === "attention"
          ? isAttentionDevice(device)
          : device.status === status);
      return matchesQuery && matchesStatus;
    });
  }, [devices, query, status]);

  const online = summaryQuery.data?.devices.online || 0;
  const offline = summaryQuery.data?.devices.offline || 0;
  const attention = summaryQuery.data?.devices.attention || 0;
  const activeTasks = summaryQuery.data?.tasks.active || 0;
  const successfulTasks = summaryQuery.data?.tasks.succeeded || 0;
  const failedTasks = summaryQuery.data?.tasks.failed || 0;
  const groups = useMemo(() => {
    const counts = new Map<string, { count: number; attention: number }>();
    devices.forEach((device) => {
      const name = device.account?.username || "未分配设备";
      const current = counts.get(name) || { count: 0, attention: 0 };
      current.count += 1;
      if (isAttentionDevice(device)) current.attention += 1;
      counts.set(name, current);
    });
    return [...counts.entries()].slice(0, 4);
  }, [devices]);

  if (devicesQuery.isError || alertsQuery.isError || summaryQuery.isError) {
    return (
      <QueryErrorState
        onRetry={() => {
          void devicesQuery.refetch();
          void alertsQuery.refetch();
          void summaryQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="overview-page">
      {!devicesQuery.isPending && !devices.length && (
        <section className="onboarding-panel">
          <div className="onboarding-copy">
            <span className="onboarding-icon"><DeviceMobile size={25} /></span>
            <div>
              <small>首次启用</small>
              <h2>连接第一台工作手机</h2>
              <p>完成设备绑定后，设备状态、任务执行和告警数据会自动出现在管理端。</p>
            </div>
          </div>
          <ol>
            <li><b>1</b><span><strong>创建设备绑定码</strong><small>前往设备管理生成二维码</small></span></li>
            <li><b>2</b><span><strong>手机扫码连接</strong><small>在 XMirror Connect 客户端完成绑定</small></span></li>
            <li><b>3</b><span><strong>下发首个任务</strong><small>验证设备与任务通道正常</small></span></li>
          </ol>
          <Link className="primary-button" to="/devices">开始连接设备</Link>
        </section>
      )}
      <section className="status-ribbon" aria-label="设备与任务摘要">
        <div>
          <span className="status-dot healthy" />
          <p><strong>在线 <b>{online}</b></strong><small>共 {devices.length} 台设备</small></p>
        </div>
        <div>
          <span className="status-dot quiet" />
          <p><strong>离线 <b>{offline}</b></strong><small>等待设备重新连接</small></p>
        </div>
        <div>
          <span className="status-dot danger" />
          <p><strong>需关注 <b>{attention}</b></strong><small>{alertsQuery.data?.activeCount || 0} 条活动告警</small></p>
        </div>
        <div className="ribbon-task">
          <p>
            <strong>任务执行</strong>
            <small>进行中 {activeTasks} · 成功 {successfulTasks} · 失败 {failedTasks}</small>
          </p>
          <span><Pulse size={28} /></span>
        </div>
      </section>

      <section className="topology-panel">
        <header className="section-header">
          <div><h2>设备拓扑</h2><p>按当前绑定账号展示设备分布</p></div>
          <SearchInput value={query} onChange={setQuery} placeholder="搜索设备或账号" />
        </header>
        <div className="topology-tree">
          <div className="topology-root">
            <TreeStructure size={19} />
            <span><strong>XMirror 企业</strong><small>{devices.length} 台设备</small></span>
          </div>
          <div className="topology-groups">
            {groups.length ? (
              groups.map(([name, summary]) => (
                <div key={name}>
                  {summary.attention ? (
                    <WarningCircle size={17} weight="fill" />
                  ) : (
                    <CheckCircle size={17} weight="fill" />
                  )}
                  <span><strong>{name}</strong><small>{summary.count} 台</small></span>
                  {summary.attention > 0 && <em>{summary.attention}</em>}
                </div>
              ))
            ) : (
              <p className="inline-empty">尚无已绑定设备</p>
            )}
          </div>
        </div>
      </section>

      <section className="table-panel">
        <header className="section-header">
          <div><h2>全部设备</h2><p>状态每 {Math.round(polling / 1000)} 秒自动刷新</p></div>
          <div className="section-actions"><DataFreshness updatedAt={summaryQuery.data?.generatedAt} isFetching={devicesQuery.isFetching || summaryQuery.isFetching} onRefresh={() => { void devicesQuery.refetch(); void alertsQuery.refetch(); void summaryQuery.refetch(); }} /><div className="segmented">
            {[
              ["all", "全部", devices.length],
              ["online", "在线", online],
              ["offline", "离线", offline],
              ["attention", "需关注", attention],
            ].map(([value, label, count]) => (
              <button
                key={value}
                className={status === value ? "active" : ""}
                onClick={() => setStatus(String(value))}
              >
                {label} <span>{count}</span>
              </button>
            ))}
          </div></div>
        </header>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>设备</th>
                <th>绑定账号</th>
                <th>状态</th>
                <th>最近心跳</th>
                <th>网络</th>
                <th>Token</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.slice(0, 12).map((device) => (
                <tr key={device.id} tabIndex={0} onClick={() => setSelectedId(device.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelectedId(device.id); }}>
                  <td>
                    <span className="device-cell">
                      <DeviceMobile size={20} />
                      <span>
                        <strong>{device.info?.model || `设备 #${device.id}`}</strong>
                        <small>{device.info?.deviceSn || device.id}</small>
                      </span>
                    </span>
                  </td>
                  <td>{device.account?.username || "未分配"}</td>
                  <td><StatusTag value={device.status} /></td>
                  <td>{formatRelativeTime(device.lastSeenAt)}</td>
                  <td>
                    <span className={device.status === "online" ? "text-green" : "text-muted"}>
                      <WifiHigh size={16} /> {device.status === "online" ? "已连接" : "—"}
                    </span>
                  </td>
                  <td>
                    {device.tokenExpired ? (
                      <StatusTag value="已过期" tone="red" />
                    ) : device.tokenExpiringSoon ? (
                      <StatusTag value="即将过期" tone="amber" />
                    ) : (
                      <span className="text-green"><BatteryMedium size={17} /> 正常</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!devicesQuery.isPending && !filteredDevices.length && (
            <div className="table-empty">没有符合当前筛选条件的设备。</div>
          )}
          {devicesQuery.isPending && <div className="table-empty">正在加载设备…</div>}
        </div>
      </section>

      {selectedId && (
        <DeviceDetail
          deviceId={selectedId}
          onClose={() => setSelectedId(null)}
          onDispatch={(device) => {
            setSelectedId(null);
            setDispatchDevice(device);
          }}
        />
      )}
      {dispatchDevice && (
        <TaskDispatchModal
          device={dispatchDevice}
          onClose={() => setDispatchDevice(null)}
        />
      )}
    </div>
  );
}
